import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { quizService } from '@/services/quizService'
import { useAppStore } from '@/stores/app.store'
import {
  CreateQuizPayload,
  CreateQuizQuestionPayload,
  ManualGradeAttemptPayload,
  QuizAttempt,
  QuizAttemptListResponse,
  QuizAttemptQueryParams,
  QuizAttemptAnswersPayload,
  QuizListResponse,
  QuizQuestion,
  QuizQuestionListResponse,
  QuizQueryParams,
  StartQuizAttemptResponse,
  SubmitQuizAttemptPayload,
  SubmitQuizAttemptResult,
  UpdateQuizPayload,
  UpdateQuizQuestionPayload,
} from '@/types/quizzes'
import { QuizAttemptModel, QuizModel } from '@/models/quiz.model'

export const quizKeys = {
  all: ['quizzes'] as const,
  lists: () => [...quizKeys.all, 'list'] as const,
  list: (filters: QuizQueryParams) => [...quizKeys.lists(), filters] as const,
  details: () => [...quizKeys.all, 'detail'] as const,
  detail: (quizId: string) => [...quizKeys.details(), quizId] as const,
  questions: (quizId: string) => [...quizKeys.detail(quizId), 'questions'] as const,
  attempts: (quizId: string) => [...quizKeys.detail(quizId), 'attempts'] as const,
  attempt: (quizId: string, attemptId: string) =>
    [...quizKeys.attempts(quizId), attemptId] as const,
  learner: {
    list: (courseId: string) => ['learner-quizzes', courseId] as const,
  },
}

const useNormalizedFilters = (filters: QuizQueryParams = {}) =>
  useMemo(() => {
    const normalized: QuizQueryParams = {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      normalized[key as keyof QuizQueryParams] = value as never
    })
    return normalized
  }, [JSON.stringify(filters)])

export const useQuizzes = (filters: QuizQueryParams = {}) => {
  const normalizedFilters = useNormalizedFilters(filters)
  const accessToken = useAppStore((state) => state.accessToken)
  return useQuery<QuizListResponse>({
    queryKey: quizKeys.list(normalizedFilters),
    queryFn: () => quizService.getQuizzes(normalizedFilters),
    enabled: Boolean(accessToken),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  })
}

export const useQuiz = (quizId: string | undefined) =>
  useQuery<QuizModel | null>({
    queryKey: quizId ? quizKeys.detail(quizId) : quizKeys.details(),
    queryFn: () => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.getQuiz(quizId)
    },
    enabled: Boolean(quizId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

export const useQuizQuestions = (quizId: string | undefined) =>
  useQuery<QuizQuestionListResponse>({
    queryKey: quizId ? quizKeys.questions(quizId) : ['quizzes', 'questions'],
    queryFn: () => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.getQuizQuestions(quizId)
    },
    enabled: Boolean(quizId),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })

export const useCreateQuiz = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateQuizPayload) => quizService.createQuiz(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: quizKeys.lists() })
    },
  })
}

export const useUpdateQuiz = (quizId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateQuizPayload) => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.updateQuiz({ ...payload, id: quizId })
    },
    onSuccess: (_, variables) => {
      if (!quizId) return
      queryClient.invalidateQueries({ queryKey: quizKeys.detail(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.questions(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.lists() })
    },
  })
}

export const useDeleteQuiz = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (quizId: string) => quizService.deleteQuiz(quizId),
    onSuccess: (_, quizId) => {
      queryClient.invalidateQueries({ queryKey: quizKeys.lists() })
      if (quizId) {
        queryClient.invalidateQueries({ queryKey: quizKeys.detail(quizId) })
      }
    },
  })
}

export const useCreateQuizQuestion = (quizId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: CreateQuizQuestionPayload) => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.createQuestion(quizId, payload)
    },
    onSuccess: () => {
      if (!quizId) return
      queryClient.invalidateQueries({ queryKey: quizKeys.questions(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.detail(quizId) })
    },
  })
}

export const useUpdateQuizQuestion = (quizId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: UpdateQuizQuestionPayload) => {
      if (!quizId) throw new Error('Quiz ID is required')
      if (!payload.id) throw new Error('Question ID is required')
      return quizService.updateQuestion(quizId, payload.id, payload)
    },
    onSuccess: (_, payload) => {
      if (!quizId) return
      queryClient.invalidateQueries({ queryKey: quizKeys.questions(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.detail(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.attempts(quizId) })
      if (payload.id) {
        queryClient.invalidateQueries({
          queryKey: quizKeys.questions(quizId),
        })
      }
    },
  })
}

export const useDeleteQuizQuestion = (quizId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (questionId: string) => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.deleteQuestion(quizId, questionId)
    },
    onSuccess: (_, questionId) => {
      if (!quizId) return
      queryClient.invalidateQueries({ queryKey: quizKeys.questions(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.detail(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.attempts(quizId) })
      if (questionId) {
        queryClient.invalidateQueries({ queryKey: [...quizKeys.questions(quizId), questionId] })
      }
    },
  })
}

const useNormalizedAttemptsFilters = (filters: QuizAttemptQueryParams = {}) =>
  useMemo(() => {
    const normalized: QuizAttemptQueryParams = {}
    Object.entries(filters).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return
      normalized[key as keyof QuizAttemptQueryParams] = value as never
    })
    return normalized
  }, [JSON.stringify(filters)])

export const useQuizAttempts = (
  quizId: string | undefined,
  filters: QuizAttemptQueryParams = {},
) => {
  const normalized = useNormalizedAttemptsFilters(filters)
  return useQuery<QuizAttemptListResponse>({
    queryKey: quizId ? [...quizKeys.attempts(quizId), normalized] : ['quizzes', 'attempts'],
    queryFn: () => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.getAttempts(quizId, normalized)
    },
    enabled: Boolean(quizId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })
}

export const useQuizAttempt = (quizId: string | undefined, attemptId: string | undefined) =>
  useQuery<QuizAttemptModel | null>({
    queryKey:
      quizId && attemptId ? quizKeys.attempt(quizId, attemptId) : ['quizzes', 'attempt', 'detail'],
    queryFn: () => {
      if (!quizId || !attemptId) throw new Error('Quiz ID and Attempt ID are required')
      return quizService.getAttempt(quizId, attemptId)
    },
    enabled: Boolean(quizId && attemptId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })

export const useLearnerQuizAttempts = (
  quizId: string | undefined,
  learnerId: string | undefined,
) =>
  useQuery<QuizAttemptListResponse>({
    queryKey:
      quizId && learnerId
        ? [...quizKeys.attempts(quizId), 'mine', learnerId]
        : ['quizzes', 'attempts', 'mine'],
    queryFn: () => {
      if (!quizId || !learnerId) throw new Error('Quiz ID and learner ID are required')
      return quizService.getAttempts(quizId, { learnerId })
    },
    enabled: Boolean(quizId && learnerId),
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
  })

export const useStartQuizAttempt = (quizId: string | undefined) =>
  useMutation<StartQuizAttemptResponse, Error, Partial<Pick<QuizAttempt, 'attempt' | 'startedAt'>>>({
    mutationFn: (options) => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.startAttempt(quizId, options)
    },
  })

export const useSubmitQuizAttempt = (quizId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation<SubmitQuizAttemptResult, Error, SubmitQuizAttemptPayload>({
    mutationFn: (payload) => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.submitAttempt(payload)
    },
    onSuccess: (result) => {
      if (!quizId) return
      queryClient.invalidateQueries({ queryKey: quizKeys.attempts(quizId) })
      if (result?.attemptId) {
        queryClient.invalidateQueries({ queryKey: quizKeys.attempt(quizId, result.attemptId) })
      }
    },
  })
}

export const useGradeQuizAttempt = (quizId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (payload: ManualGradeAttemptPayload) => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.gradeAttempt(payload)
    },
    onSuccess: (result) => {
      if (!quizId) return
      queryClient.invalidateQueries({ queryKey: quizKeys.attempts(quizId) })
      queryClient.invalidateQueries({ queryKey: quizKeys.detail(quizId) })
      if (result?.id) {
        queryClient.invalidateQueries({ queryKey: quizKeys.attempt(quizId, result.id) })
      }
    },
  })
}

export const useDeleteQuizAttempt = (quizId: string | undefined) => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (attemptId: string) => {
      if (!quizId) throw new Error('Quiz ID is required')
      return quizService.deleteAttempt(quizId, attemptId)
    },
    onSuccess: (_, attemptId) => {
      if (!quizId) return
      queryClient.invalidateQueries({ queryKey: quizKeys.attempts(quizId) })
      if (attemptId) {
        queryClient.invalidateQueries({ queryKey: quizKeys.attempt(quizId, attemptId) })
      }
    },
  })
}

export const useBuildAttemptPayload = (
  quizId: string | undefined,
  attempt: QuizAttemptModel | null | undefined,
  questions: QuizQuestion[] | undefined,
) => {
  return (responses: QuizAttemptAnswersPayload['questions'], meta?: QuizAttemptAnswersPayload['meta']) => {
    if (!quizId || !attempt || !questions) {
      throw new Error('Quiz attempt context is not ready')
    }
    return quizService.buildAttemptAnswersPayload(
      attempt,
      questions as QuizQuestion[],
      responses,
      undefined,
      undefined,
      meta ?? undefined,
    )
  }
}
